import { CUSTOM_ELEMENTS_SCHEMA, Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChange, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, startWith, map } from 'rxjs';
import { CommonModule } from '@angular/common';

/**
 * Notes:
 * 1) we emit an event, so the parent can do something. ex:  place values in storage.
 * 2) there are currently issues with "key" values that are numbers. The code assumes strings (.includes, .indexOf etc).
 *    but even with a "-1"  as a key values. the return from the formControl is a number.  This needs to be fixed.
 */
@Component({
  selector: 'so-filtering-multiselect',
  templateUrl: 'so-filtering-multiselect.html',
  styleUrls: ['./so-filtering-multiselect.scss'],

  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    MatSelectModule,
    ReactiveFormsModule,],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SoFilteringMultiSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  searchCtrl!: FormControl;
  @Input()
  selectOptions!: Record<string, string>[];
  @Input() dataFormCtrl = new FormControl<string[]>([]);
  @Input() filteredOptions!: Observable<Record<string, string>[]>;
  @Input() label = "";
  @Input() filterName= "";
  @Input() allowSelectAll = true;
  @Input() toolTip = "";
  @Output() changeEvent = new EventEmitter<string[]>();

  //constructor() { }

  //When I change values in 'Main', the on change detects the error, but the UI is not updating.
  ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    //console.log('ngOnChanges = ', changes['selectOptions']);
    //Trigger the filtering by changing the searchValue
    this.setFilterOptions();
  }

  ngOnDestroy(): void {
    //console.log("destroy FS");
    this.searchCtrl = new FormControl('');
  }

  /*
  ngAfterViewInit(): void {
    //this.filteredOptions.subscribe((v) => console.log('got new heroes list: ', v));

  }
*/
  ngOnInit(): void {
    this.setFilterOptions();
  }

  setFilterOptions(): void {
    this.filteredOptions = this.searchCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterOptions(value || '', this.selectOptions))
    );
  }
  /**
    * Filter a list of options whenver the search value changes.
    * @param filterString filter string
    * @param selectOptions array of Records
    * @returns array of Filtered Records
    */
  filterOptions(filterString: string, selectOptions?: Record<string, string>[]): Record<string, string>[] {
    const filterValue = filterString.toLowerCase();
    if (selectOptions) {
      return selectOptions?.filter((selectOptions) =>
        selectOptions['value'].toLowerCase().includes(filterValue)
      );
    } else {
      return [];
    }
  }

  /*
    private _filter(value: string, options: string[]): string[] {
      const filterValue = value.toLowerCase();
      return options?.filter((option) =>
        option.toLowerCase().includes(filterValue)
      );
    }
  */
  /**
    * toggleSelection: select all elements of a select if not all are selected, otherwise deselect all.
    * @param e MatSelect
    * @returns
    */
  selected(e: MatSelect): 'some' | 'all' | 'none' | undefined {
    let ret: 'some' | 'all' | 'none' | undefined = undefined;
    if (e.options == null || e.options.length === 0) {
      //return undefined;
    } else if (e._selectionModel.selected.length === e.options.length) {
      ret = 'all';
    } else if (
      e._selectionModel.selected.length > 0 &&
      e._selectionModel.selected.length < e.options.length
    ) {
      ret = 'some';
    } else {
      ret = 'none';
    }

    /*
    if (!this.allowSelectAll && ret == 'some') {
      ret = 'all';
    }
    */
    return ret;
  }

  /**
   * toggleSelection: select all options of a select if not all are selected, otherwise it deselects all.
   *  if allowSelectAll is 'false' , then if all the options are selected, then none are selected.
   *  @param evt MatCheckboxChange
   * @param e MatSelect
   v* @param chkBox MatCheckBox
   * @param chkBox
   */
  //<mat-option value="1">PUBLIC</mat-option>   MAT-OPTION -> value is the the key
  toggleSelection(evt: MatCheckboxChange, e: MatSelect, chkBox: MatCheckbox) {
    let values: string[] = this.dataFormCtrl.value || ([] as string[]);


    const priorSelectState = this.selected(e);
    //if all the items were selected, then unselect them all.
    if (priorSelectState === 'all') {
      values = [];
    } else {
      //loop though all the current options (could be filtered) and select them
      e.options.forEach((item: MatOption) => {
        if (!values.includes(item.value)) {
          values.push(item.value);
        }
        //else if (selectState === 'all' && values?.includes(item.value)) {
        //  values = values.filter((value) => value !== item.value);
        // }
      });
    }
    //If the list of options is NOT filtered, and we disallow selecting all, then reset values to nothing.
    if (!this.allowSelectAll && this.searchCtrl.value == "" && 
        e.options.length == values.length) {
      values = [];
    }

    this.dataFormCtrl?.setValue(values);
    this.changeEvent.emit(values);

    //Since we cleared all the values is in this case, make sure the checkbox is not checked.
    if (!this.allowSelectAll) {
      chkBox.checked = false;
    }

  }

  /**
    * Event when an option is selected.
    * @param a MatOptionSelectionChange
    */
  onSelectionChange(a: MatOptionSelectionChange) {
    let values: string[] = this.dataFormCtrl?.value || ([] as string[]);
    if (a.isUserInput) {
      if (a.source.selected && !values.includes(a.source.value)) {
        values.push(a.source.value);
      } else if (!a.source.selected && values.includes(a.source.value)) {
        values = values.filter((value) => value !== a.source.value);
      }
      this.dataFormCtrl?.setValue(values);
    }
    this.changeEvent.emit(values);
  }
  /**
   * set display for mat-select-trigger picking 1st element and counting the rest.
   * @returns
   */
  selectDisplay() {
    //remember 'value' is a list of select row keys.  We want to display related values.
    let ret = "";
    if (this.dataFormCtrl) {
      const keyVal = this.dataFormCtrl.value?.[0];

      if (keyVal !== undefined && this.selectOptions) {
        ret = this.selectOptions.filter(function (row) {
          return row['key'] == keyVal;
        })[0]['value'];
        if ((this.dataFormCtrl.value?.length || 0) > 1) {
          ret += "<span class='additional-selection'> (+" + ((this.dataFormCtrl.value?.length || 0) - 1);
          ret += this.dataFormCtrl.value?.length === 2 ? ' other' : ' others';
          ret += ")</span>";

        }
      }
    }

    return ret;
  }

  /**
   * Dynamically create a tooltip based on all selected values.
   * @returns
   */
  selectToolTip(): string {

    let selectedValues: Record<string, string>[] = [];
    let retStr = "";
    const values: string[] = [];
    if (this.dataFormCtrl) {
      const selectedKeys = this.dataFormCtrl.value;
      if (this.selectOptions) {
        selectedValues = this.selectOptions.filter(function (row) {
          return selectedKeys && selectedKeys?.indexOf(row['key']) > -1;
        });
      }

      for (const row of selectedValues) {
        values.push(row['value']);
      }
      retStr = values.join(", ");
    }


    return retStr ? retStr : this.toolTip || "no selections";
  }

  clearInput(evt: Event) {
    this.dataFormCtrl.setValue(null);
    evt.stopPropagation();
  }

  setHeaderClass() {
    return this.dataFormCtrl.value === null  || this.dataFormCtrl.value?.length === 0  ? ' ' : 'selectheaderWithData';
  }

  /**
   * sets intdetermine state of select all checkbox
   * @param e 
   * @returns 
   */
  selectAllIndeterminate(e: MatSelect) {
    const selectState = this.selected(e);
    return (this.allowSelectAll && selectState === 'some') ||
        !this.allowSelectAll && selectState != 'none';
  }

  selectAllToolTip(e: MatSelect) {
    const selectState = this.selected(e);
    if (!this.allowSelectAll && selectState !== 'some') return "";
    return (selectState === 'all' || !this.allowSelectAll) ? 'Unselect All' : 'Select All';
  }
  selectAllClickable(evt: MouseEvent, e: MatSelect) {
    //If you don't want select all, then don't allow clicking of button.
    /*
    if (!this.allowSelectAll) {
      evt.preventDefault();
      return;
    }
        */
    const selectState = this.selected(e);
    if (selectState !== 'some' && !this.allowSelectAll) {
      evt.preventDefault();
    } 
    /*
     if (this.allowSelectAll)  {
         e.value = false;
   
     } else if ( selectState === 'all' && !this.allowSelectAll){
       evt.preventDefault();
     } else {
      evt.preventDefault();
     }
     */

  }

  selectAllChecked(e: MatSelect) {
    if (!this.allowSelectAll) {
      return false;
    } else {
      const selectState = this.selected(e);
      return selectState === 'all';
    }
  }

  //-----------------------------------------------------------
  // Search Box
  //-----------------------------------------------------------
  onSearchKeydown(e: KeyboardEvent, i: HTMLInputElement) {
    i.onkeydown?.(e);
    e.stopPropagation();
  }
  clearSearch() {
    this.searchCtrl.setValue("");
  }


}

/**  Copyright 2023 Google LLC. All Rights Reserved.
    Use of this source code is governed by an MIT-style license that
    can be found in the LICENSE file at https://angular.io/license */
